import { Box, Flex, FormLabel, HStack, Input, SimpleGrid, Switch, Text } from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { FormField } from '../../../../components/FormField'
import { MoneyInput } from '../../../../components/MaskedInput'
import { FormSelect } from '../../../../components/Select'
import { TCommissionType, TFrameValueMode } from '../../../../services/Contests/interfaces'
import ArtFieldsSummary from '../ArtFieldsSummary'
import { IContestFormTabProps } from './types'

interface IProps extends IContestFormTabProps {
  minArtsEnabled: boolean
  setMinArtsEnabled: Dispatch<SetStateAction<boolean>>
  maxArtsEnabled: boolean
  setMaxArtsEnabled: Dispatch<SetStateAction<boolean>>
  hasArtFieldErrors: boolean
  onEditArtFields: () => void
  /** ABRA-338 — the auction already has bids, so the system fee can no longer change. */
  systemFeeLocked: boolean
}

export function ArtworksTab({
  form,
  setForm,
  errors,
  saving,
  minArtsEnabled,
  setMinArtsEnabled,
  maxArtsEnabled,
  setMaxArtsEnabled,
  hasArtFieldErrors,
  onEditArtFields,
  systemFeeLocked
}: IProps) {
  return (
    <Flex direction="column" gap={3}>
      <ArtFieldsSummary
        fields={form.art_fields ?? []}
        notice={form.art_fields_notice ?? null}
        onEdit={onEditArtFields}
        disabled={saving}
        hasErrors={hasArtFieldErrors}
      />

      {/* ABRA-92 — optional min/max number of artworks each registrant can submit. */}
      <Box>
        <FormField label="Limites de obras por inscrito">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
              <HStack justify="space-between" align="center">
                <FormLabel htmlFor="min_arts_switch" mb={0} fontSize="sm">
                  Adicionar limite mínimo de artes enviadas
                </FormLabel>
                <Switch
                  id="min_arts_switch"
                  isChecked={minArtsEnabled}
                  isDisabled={saving}
                  onChange={(e) => {
                    setMinArtsEnabled(e.target.checked)
                    setForm({
                      ...form,
                      min_arts_per_application: e.target.checked ? (form.min_arts_per_application ?? 1) : null
                    })
                  }}
                />
              </HStack>
              <FormField mt={2} error={errors.min_arts_per_application}>
                <Input
                  type="number"
                  min={1}
                  placeholder="Mínimo de obras"
                  value={form.min_arts_per_application ?? ''}
                  isDisabled={saving || !minArtsEnabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      min_arts_per_application:
                        e.target.value === '' ? null : Math.max(1, Math.floor(Number(e.target.value) || 1))
                    })
                  }
                />
              </FormField>
            </Box>

            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
              <HStack justify="space-between" align="center">
                <FormLabel htmlFor="max_arts_switch" mb={0} fontSize="sm">
                  Adicionar limite máximo de artes enviadas
                </FormLabel>
                <Switch
                  id="max_arts_switch"
                  isChecked={maxArtsEnabled}
                  isDisabled={saving}
                  onChange={(e) => {
                    setMaxArtsEnabled(e.target.checked)
                    setForm({
                      ...form,
                      max_arts_per_application: e.target.checked ? (form.max_arts_per_application ?? 1) : null
                    })
                  }}
                />
              </HStack>
              <FormField mt={2} error={errors.max_arts_per_application}>
                <Input
                  type="number"
                  min={1}
                  placeholder="Máximo de obras"
                  value={form.max_arts_per_application ?? ''}
                  isDisabled={saving || !maxArtsEnabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      max_arts_per_application:
                        e.target.value === '' ? null : Math.max(1, Math.floor(Number(e.target.value) || 1))
                    })
                  }
                />
              </FormField>
            </Box>
          </SimpleGrid>
        </FormField>
        <Text variant="caption" mt={1}>
          Opcional. Quando ativado, limita o número de obras que cada inscrito pode enviar no concurso.
        </Text>
      </Box>

      {/* ABRA-191 — molduras vendidas junto com as obras no leilão. O valor
          é resolvido por referência: a obra guarda apenas uma exceção, então
          alterar o valor único aqui alcança todas as obras não editadas à mão. */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <FormLabel htmlFor="frame_value_mode" mb={0} fontSize="sm">
            Valor da moldura
          </FormLabel>
          {/* Largura fixa dimensionada pela opção mais longa ("Valor único
              definido pela ABRA") — com menos que isso o texto fica cortado
              pelo ícone do select. */}
          <FormSelect
            id="frame_value_mode"
            w="320px"
            size="sm"
            isDisabled={saving}
            value={form.frame_value_mode ?? 'disabled'}
            onChange={(value) => {
              const frameValueMode = value as TFrameValueMode
              // Leaving the "fixed" mode drops the single value so we never
              // persist a value that no artwork can reach.
              if (frameValueMode !== 'fixed') {
                setForm({ ...form, frame_value_mode: frameValueMode, frame_value: null })
                return
              }
              setForm({ ...form, frame_value_mode: frameValueMode })
            }}
            options={[
              { value: 'disabled', label: 'Desativado' },
              { value: 'per_art', label: 'O aluno informa no envio' },
              { value: 'fixed', label: 'Valor único definido pela ABRA' }
            ]}
            hideEmptyOption
          />
        </HStack>

        {form.frame_value_mode === 'fixed' && (
          <Box mt={3}>
            <FormField label="Valor único da moldura" htmlFor="frame_value" error={errors.frame_value}>
              <MoneyInput
                value={form.frame_value ?? null}
                onChange={(frame_value) => setForm({ ...form, frame_value })}
                isDisabled={saving}
              />
            </FormField>
          </Box>
        )}

        <Text variant="caption" mt={1}>
          Opcional. Quando ativado, o valor da moldura aparece na lista das obras ao lado do valor da obra e pode ser
          ajustado obra a obra pela equipe. No modo &quot;valor único&quot;, alterar o valor aqui atualiza todas as
          obras que não tiveram o valor editado manualmente.
        </Text>
      </Box>

      {/* ABRA-337 — lance inicial fixo do leilão. Diferente da moldura, o valor é gravado
          em cada obra, então alterá-lo reescreve as obras já enviadas (bloqueado pela API
          quando o leilão já tem lances). */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <FormField
          label="Lance inicial padrão do leilão"
          htmlFor="default_starting_bid"
          error={errors.default_starting_bid}
        >
          <MoneyInput
            id="default_starting_bid"
            value={form.default_starting_bid ?? null}
            onChange={(default_starting_bid) => setForm({ ...form, default_starting_bid })}
            isDisabled={saving}
          />
        </FormField>
        <Text variant="caption" mt={1}>
          Opcional. Quando preenchido, todas as obras enviadas para leilão têm este lance inicial e o inscrito não pode
          alterá-lo. Trocar para outro valor atualiza também as obras já enviadas. Deixe vazio para o inscrito escolher
          o lance (de R$ 100 a R$ 650); as obras já enviadas mantêm o lance que têm.
        </Text>
      </Box>

      {/* Taxa da ABRA sobre cada obra vendida no leilão. Porcentagem: sai do repasse ao aluno
          (repasse, e-mail de resultado, planilha e aviso no envio das obras). Valor fixo: é a taxa
          sistêmica (ABRA-338), paga pelo comprador por cima do lance — o tablet a mostra na
          confirmação do lance e o checkout a cobra. Quem deu lance viu a taxa, então a API recusa
          mudá-la depois do primeiro lance. */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <FormLabel htmlFor="commission_type" mb={0} fontSize="sm">
            Taxa da ABRA sobre a venda
          </FormLabel>
          <FormSelect
            id="commission_type"
            w="320px"
            size="sm"
            isDisabled={saving || systemFeeLocked}
            value={form.commission_type ?? 'percent'}
            onChange={(value) => setForm({ ...form, commission_type: value as TCommissionType })}
            options={[
              { value: 'percent', label: 'Porcentagem, descontada do aluno' },
              { value: 'fixed', label: 'Valor fixo, pago pelo comprador' }
            ]}
            hideEmptyOption
          />
        </HStack>

        <Box mt={3}>
          <FormField
            label={form.commission_type === 'fixed' ? 'Valor da taxa (R$)' : 'Porcentagem da taxa (%)'}
            htmlFor="commission_value"
            error={errors.commission_value || errors.commission_type}
          >
            {form.commission_type === 'fixed' ? (
              <MoneyInput
                id="commission_value"
                value={form.commission_value ?? null}
                onChange={(commission_value) => setForm({ ...form, commission_value: commission_value ?? 0 })}
                isDisabled={saving || systemFeeLocked}
              />
            ) : (
              <Input
                id="commission_value"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.commission_value ?? ''}
                onChange={(e) =>
                  setForm({ ...form, commission_value: e.target.value === '' ? 0 : Number(e.target.value) })
                }
                isDisabled={saving}
              />
            )}
          </FormField>
        </Box>
        <Text variant="caption" mt={1}>
          {form.commission_type === 'fixed'
            ? 'Taxa sistêmica somada a cada obra arrematada e cobrada do comprador no checkout; o tablet mostra o lance, a taxa e o total na confirmação do lance. O aluno recebe o lance inteiro. Use 0 para não cobrar taxa.'
            : 'Parte do valor de venda que a ABRA retém de cada obra; o aluno recebe o restante. Use 0 para repassar 100% ao aluno.'}
          {systemFeeLocked && ' Como o leilão já possui lances, não é possível trocar o tipo nem mudar a taxa fixa.'}
        </Text>
      </Box>

      {/* Multa de R$ 200 + R$ 7/dia por atraso na retirada, nos termos de entrega do site e
          no identificador da obra. Desligada quando as obras não ficam com a ABRA (kids/teens:
          os professores guardam e devolvem em sala). */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <FormField label="Cobrar multa e taxa de armazenamento" htmlFor="charge_storage_fee_switch" mb={0}>
            <Switch
              id="charge_storage_fee_switch"
              isChecked={form.charge_storage_fee ?? true}
              isDisabled={saving}
              onChange={(e) => setForm({ ...form, charge_storage_fee: e.target.checked })}
            />
          </FormField>
        </HStack>
        <Text variant="caption" mt={1}>
          Quando ativado, os termos de entrega e o identificador da obra informam a multa de R$ 200,00 mais R$ 7,00 por
          dia de armazenamento para obras retiradas fora do prazo.
        </Text>
      </Box>

      {/* Segments the submitted work type (Desenho/Ilustração) and asks for a
          concept description only for illustrations. When off, the public
          submission form behaves exactly as before. */}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
        <HStack justify="space-between" align="center">
          <FormField label="Segmentar tipo de trabalho (Desenho / Ilustração)" htmlFor="use_work_type_switch" mb={0}>
            <Switch
              id="use_work_type_switch"
              isChecked={form.use_work_type ?? false}
              isDisabled={saving}
              onChange={(e) => setForm({ ...form, use_work_type: e.target.checked })}
            />
          </FormField>
        </HStack>
        <Text variant="caption" mt={1}>
          Opcional. Quando ativado, o inscrito escolhe o tipo de trabalho ao enviar a obra e, para ilustrações, descreve
          o conceito da obra (até 700 caracteres).
        </Text>
      </Box>
    </Flex>
  )
}
